import { Suspense, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { captureException } from "@sentry/react";
import UpdateSplash from "../VersionCheck/UpdateSplash";
import { isStaleChunkError, recoverFromStaleBuild } from "../../utils/appVersion";
import { lazyWithRecovery } from "../../utils/lazyWithRecovery";

// Lazy on purpose: routeComponents also splits Error500 into its own chunk, and a static
// import from the entry makes that chunk and the entry import each other, which crashes
// the production bundle on start-up.
const Error500 = lazyWithRecovery(() => import("../../modules/errors/components/Error500"));

interface AppErrorFallbackProps {
  error: unknown;
}

/**
 * Fallback for the top-level error boundary. A stale-chunk error is a deploy symptom, not
 * an application fault: reload onto the new build, and report it only if the reload was
 * refused. Any other error shows the crash screen and is reported once.
 */
const AppErrorFallback = ({ error }: AppErrorFallbackProps) => {
  const [isRecovering, setIsRecovering] = useState(() => isStaleChunkError(error));
  // useState initialisers run once, so re-renders don't file the same error again.
  const [errorId, setErrorId] = useState(() => (isStaleChunkError(error) ? "" : captureException(error)));

  useEffect(() => {
    if (!isRecovering) return;
    let isMounted = true;
    void recoverFromStaleBuild().then((didReload) => {
      if (didReload || !isMounted) return;
      setErrorId(captureException(error));
      setIsRecovering(false);
    });
    return () => {
      isMounted = false;
    };
    // Recovery runs once for the error this fallback was mounted with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isRecovering) return <UpdateSplash message="Refreshing application..." />;

  return (
    <Suspense
      fallback={
        <div className="d-flex flex-center h-100 w-100">
          <Spinner animation="border" />
        </div>
      }
    >
      <div className="d-flex flex-column flex-root">
        <div className="d-flex flex-column flex-center flex-column-fluid">
          <div className="d-flex flex-column flex-center p-10">
            <div className="card card-flush w-lg-650px">
              <div className="card-body">
                <Error500 errorId={errorId} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Suspense>
  );
};

export default AppErrorFallback;
